/**
 * Work Location Help Content
 *
 * Covers daily location declarations for hybrid work.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN WORK LOCATION HELP
// =============================================================================

export const workLocationHelp: HelpContent = createHelpContent({
  id: 'work-location',
  title: 'Work Location',
  description: 'Declare your daily work location for hybrid work tracking',
  lastUpdated: '2026-02-08',
  version: '1.0',

  tips: [
    createTip(
      'tip-default',
      'pro-tip',
      'Set a Default',
      'If you usually work from the same location, set a default to reduce daily updates. You can always override it for specific days.'
    ),
    createTip(
      'tip-advance',
      'info',
      'Plan Ahead',
      'You can set your work location for future dates. This helps your team plan in-person meetings and collaboration days.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Work Location page lets you declare where you'll be working each day. This supports hybrid work tracking and helps teams coordinate office presence.</p>
      <p>Available location types:</p>
      <ul>
        <li><strong>Office</strong> — Working from the company office</li>
        <li><strong>Home</strong> — Working from home (remote)</li>
        <li><strong>Client Site</strong> — Working at a client's location</li>
      </ul>`
    ),
    createSection(
      'declaring',
      'Declaring Your Location',
      `<p>Set your work location for each day:</p>
      <ul>
        <li>Click on a date in the monthly calendar</li>
        <li>Select your location type from the options</li>
        <li>You can set locations for future dates in advance</li>
        <li>Past dates can be updated if you forgot to declare</li>
      </ul>
      <p>Color coding on the calendar makes it easy to see your location pattern at a glance.</p>`
    ),
    createSection(
      'calendar',
      'Monthly Calendar View',
      '<p>The calendar shows your declared locations for the entire month. Each day is color-coded by location type. Navigate between months to view past declarations or set future ones.</p>'
    ),
    createSection(
      'team-view',
      'Team View',
      '<p>Managers can view the work locations for their team members. This helps plan team activities, office space, and in-person meetings on days when most of the team is in the office.</p>'
    ),
  ],

  faqs: [
    createFAQ(
      'faq-change-past',
      'Can I change a past day\'s location?',
      'Yes, you can update past declarations. Click on the date and select the correct location. This is useful if you forgot to set your location or plans changed.',
      ['change', 'past', 'update', 'edit']
    ),
    createFAQ(
      'faq-default',
      'Can I set a default work location?',
      'If a default setting is available, it will pre-fill your calendar. You can override any specific day. Check your profile settings for default location options.',
      ['default', 'preset', 'automatic']
    ),
    createFAQ(
      'faq-manager-visibility',
      'Can my manager see where I work?',
      'Yes, managers can view team members\' declared work locations to help with planning. This is part of hybrid work coordination.',
      ['manager', 'see', 'visibility', 'privacy']
    ),
    createFAQ(
      'faq-required',
      'Is declaring my location mandatory?',
      'This depends on your company\'s hybrid work policy. Some organizations require daily declarations while others make it optional. Check with your HR team.',
      ['required', 'mandatory', 'must', 'policy']
    ),
    createFAQ(
      'faq-leave-days',
      'What about days I\'m on leave?',
      'Leave days are automatically shown on the calendar. You don\'t need to set a work location for days when you have approved leave.',
      ['leave', 'vacation', 'off', 'holiday']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Leave Management', '/leave', { description: 'Request and manage time off' }),
    createRelatedLink('Leave Approvals', '/leave-approvals', { description: 'Approve team leave requests' }),
    createRelatedLink('Dashboard', '/', { description: 'View your dashboard' }),
  ],

  keywords: [
    'work location',
    'WFH',
    'office',
    'hybrid',
    'remote',
    'client site',
    'location tracking',
    'home',
    'where',
  ],
});
